'use client';

import { CaretDown } from 'phosphor-icons';
import { c } from '../../../utils/tailwind-merge';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '../DropDownMenu';
import { MobileFilterModal } from './MobileFilterModal';
import {
  ProductListFilterValues,
  ProductListSortValues,
  type ProductListSortingOption,
  type ProductListSortingProps,
} from './ProductListSorting.types';

type FilterDropdownProps = {
  options: ProductListSortingOption[];
  selectedValues: string[];
  onValuesChange: (values: string[]) => void;
  ariaLabel: string;
  label: string;
};

const FilterDropdown = ({
  options,
  selectedValues,
  onValuesChange,
  ariaLabel,
  label,
}: FilterDropdownProps) => {
  const count = selectedValues.length;
  const displayLabel = count > 0 ? `${label}(${count})` : label;

  const handleCheckedChange = (value: string, checked: boolean) => {
    if (checked) {
      onValuesChange([...selectedValues, value]);
    } else {
      onValuesChange(selectedValues.filter((v) => v !== value));
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex min-w-[160px] items-center justify-between gap-x-2 rounded-lg border border-grey-400 bg-white px-3 py-2.5 text-sm font-medium text-grey-700 outline-offset-2 hover:text-grey-900"
          aria-label={ariaLabel}
        >
          <span className="truncate">{displayLabel}</span>
          <CaretDown size={15} className="flex-shrink-0" weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[8rem]">
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={selectedValues.includes(option.value)}
            onCheckedChange={(checked) =>
              handleCheckedChange(option.value, checked === true)
            }
            onSelect={(e) => e.preventDefault()}
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

type SortDropdownProps = {
  options: ProductListSortingOption[];
  selectedValue: string | null;
  onValueChange: (value: string) => void;
  ariaLabel: string;
  placeholder: string;
};

const SortDropdown = ({
  options,
  selectedValue,
  onValueChange,
  ariaLabel,
  placeholder,
}: SortDropdownProps) => {
  const selectedOption = options.find((opt) => opt.value === selectedValue);
  const displayLabel = selectedOption?.label || placeholder;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="flex min-w-[160px] items-center justify-between gap-x-2 rounded-lg border border-grey-400 bg-white px-3 py-2.5 text-sm font-medium text-grey-700 outline-offset-2 hover:text-grey-900"
          aria-label={ariaLabel}
        >
          <span className="truncate">{displayLabel}</span>
          <CaretDown size={15} className="flex-shrink-0" weight="bold" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[8rem]">
        <DropdownMenuRadioGroup
          value={selectedValue || ''}
          onValueChange={onValueChange}
        >
          {options.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export const ProductListSorting = ({
  selectedSort,
  selectedFilters,
  resultsCount = 0,
  onSortChange,
  onFiltersChange,
  onClear,
  microcopy,
  className = '',
}: ProductListSortingProps) => {
  if (microcopy.filterOptions.length !== 5) {
    throw new Error('filterOptions microcopy must have exactly 5 items');
  }
  if (microcopy.sortOptions.length !== 6) {
    throw new Error('sortOptions microcopy must have exactly 6 items');
  }

  const filterOptions: ProductListSortingOption[] = [
    {
      label: microcopy.filterOptions[0],
      value: ProductListFilterValues.ON_SALE,
    },
    {
      label: microcopy.filterOptions[1],
      value: ProductListFilterValues.NEW_RELEASE_DISCOUNT,
    },
    {
      label: microcopy.filterOptions[2],
      value: ProductListFilterValues.QUICK_START_BUNDLE,
    },
    {
      label: microcopy.filterOptions[3],
      value: ProductListFilterValues.ON_SALE_SOON,
    },
    {
      label: microcopy.filterOptions[4],
      value: ProductListFilterValues.PUBLISHER_OF_THE_WEEK,
    },
  ];

  const sortOptions: ProductListSortingOption[] = [
    {
      label: microcopy.sortOptions[0],
      value: ProductListSortValues.POPULARITY,
    },
    { label: microcopy.sortOptions[1], value: ProductListSortValues.RATING },
    {
      label: microcopy.sortOptions[2],
      value: ProductListSortValues.PUBLISHED_DATE,
    },
    {
      label: microcopy.sortOptions[3],
      value: ProductListSortValues.RECENTLY_UPDATED,
    },
    {
      label: microcopy.sortOptions[4],
      value: ProductListSortValues.PRICE_LOW_TO_HIGH,
    },
    {
      label: microcopy.sortOptions[5],
      value: ProductListSortValues.PRICE_HIGH_TO_LOW,
    },
  ];

  return (
    <div className={c('', className)} data-test="product-list-sorting">
      <div className="hidden items-center gap-4 lg:flex">
        <FilterDropdown
          options={filterOptions}
          selectedValues={selectedFilters}
          onValuesChange={onFiltersChange}
          ariaLabel={microcopy.filterAriaLabel}
          label={microcopy.mobileFilterTitle}
        />
        <SortDropdown
          options={sortOptions}
          selectedValue={selectedSort}
          onValueChange={onSortChange}
          ariaLabel={microcopy.sortAriaLabel}
          placeholder={microcopy.sortPlaceholder}
        />
      </div>

      <div className="lg:hidden">
        <MobileFilterModal
          filterOptions={filterOptions}
          sortOptions={sortOptions}
          selectedFilters={selectedFilters}
          selectedSort={selectedSort}
          onFiltersChange={onFiltersChange}
          onSortChange={onSortChange}
          onClear={onClear}
          resultsCount={resultsCount}
          microcopy={microcopy}
        />
      </div>
    </div>
  );
};

ProductListSorting.displayName = 'ProductListSorting';
